/**
 * The one parser for the reason a hub receipt or bill was held.
 *
 * Pure and dependency-free on purpose: the console imports this file directly
 * (the same way card-presenter.ts imports am_visible_text.ts), and the Worker's
 * "everything the hub sent" read will import it too. The card and that list
 * must never disagree about which reason an item has.
 *
 * The stored `review_queue.reason` is generic (`low-confidence`, `anomaly`,
 * `new-vendor`, `receipt_card_not_attached`). The real hold reason is the
 * first non-reserved line of `ai_rationale`. Reserved leading lines (ARF1,
 * HGL1, LS1) are recovery / hub-applied / loan-statement claims and are
 * skipped, exactly as card-presenter.ts skips them.
 *
 * Three receipt keys are derived from `receiptContext`, and the three receipt
 * match-blocker keys come from a card subject: this parser never returns
 * those. Keeping them out means one decision point per key.
 */

const BOOKING_FAILURE_LINE_PREFIX = "ARF1";
const HUB_APPLIED_LINE_PREFIX = "HGL1";
const LOAN_STATEMENT_LINE_PREFIX = "LS1";

const RESERVED_PREFIXES = [
  BOOKING_FAILURE_LINE_PREFIX,
  HUB_APPLIED_LINE_PREFIX,
  LOAN_STATEMENT_LINE_PREFIX,
] as const;

export const HOLD_REASON_CODES = [
  "receipt_gl_account_unmapped",
  "receipt_review_flag",
  "receipt_location_unconfigured",
  "receipt_account_not_open",
  "receipt_account_is_bank",
  "receipt_amount_date_disagrees",
  "receipt_before_books_start",
  "receipt_evidence_refused",
  "receipt_attachment_unreadable",
  "receipt_category_unreadable",
  "receipt_lookup_failed",
  "receipt_card_not_attached",
  "receipt_shared_card_outside_set",
  "receipt_needed_for_vendor",
  "receipt_not_confirmed",
  "receipt_did_not_book",
  "receipt_match_conflict",
  "receipt_match_refused",
  "receipt_match_review",
  "bill_amount_disagrees",
  "bill_line_unlocated",
  "bill_location_not_in_ledger",
  "bill_multiple_locations",
  "bill_unmapped_gl_account",
  "bill_account_not_in_chart",
  "bill_line_maps_to_bank",
  "bill_judgment_account",
  "bill_period_locked",
  "bill_match_ambiguous",
  "bill_lookup_failed",
] as const;

export type HoldReasonCode = (typeof HOLD_REASON_CODES)[number];

/** Receipt tokens written as the whole first rationale line. */
const RECEIPT_TOKEN_CODES: ReadonlySet<string> = new Set<HoldReasonCode>([
  "receipt_gl_account_unmapped",
  "receipt_review_flag",
  "receipt_location_unconfigured",
  "receipt_account_not_open",
  "receipt_account_is_bank",
  "receipt_amount_date_disagrees",
  "receipt_before_books_start",
  "receipt_evidence_refused",
  "receipt_attachment_unreadable",
  "receipt_category_unreadable",
  "receipt_lookup_failed",
]);

/** The `HoldReason` members in pipeline/bill_payment.py. */
const BILL_HOLD_REASONS: ReadonlySet<string> = new Set([
  "amount_disagrees",
  "line_unlocated",
  "multiple_locations",
  "location_not_in_ledger",
  "unmapped_gl_account",
  "account_not_in_chart",
  "line_maps_to_bank",
  "judgment_account",
  "period_locked",
  "match_ambiguous",
  "lookup_failed",
]);

/**
 * Wallet codes that only a bill payment can produce. The Moov path also
 * produces `amount_disagrees`, `account_not_in_chart`, `judgment_account` and
 * `period_locked` for other movements, so those are deliberately absent: a
 * wallet card carrying one of them is not necessarily a bill.
 */
export const BILL_HOLD_WALLET_CODES: Readonly<Record<string, HoldReasonCode>> =
  Object.freeze({
    line_unlocated: "bill_line_unlocated",
    location_not_in_ledger: "bill_location_not_in_ledger",
    multiple_locations: "bill_multiple_locations",
    unmapped_gl_account: "bill_unmapped_gl_account",
    line_maps_to_bank: "bill_line_maps_to_bank",
    lookup_failed: "bill_lookup_failed",
  });

function dropTrailingCR(line: string): string {
  return line.endsWith("\r") ? line.slice(0, -1) : line;
}

function isReservedRationaleLine(line: string): boolean {
  const body = dropTrailingCR(line);
  return RESERVED_PREFIXES.some(
    (prefix) => body === prefix || body.startsWith(`${prefix}\t`),
  );
}

/** First non-empty, non-reserved line, or null. Never returns a later line. */
function firstUsefulLine(text: string | null | undefined): string | null {
  if (text === null || text === undefined) return null;
  for (const raw of text.split("\n")) {
    const line = dropTrailingCR(raw);
    if (line.length === 0) continue;
    if (isReservedRationaleLine(line)) continue;
    return line;
  }
  return null;
}

/**
 * The hold key for a stored rationale, or null when this is not a recognizable
 * receipt or bill hold.
 */
export function holdReasonFromRationale(
  text: string | null | undefined,
): HoldReasonCode | null {
  const line = firstUsefulLine(text);
  if (line === null) return null;
  if (RECEIPT_TOKEN_CODES.has(line)) return line as HoldReasonCode;
  if (/^receipt_gl_account_unmapped(: .*)?$/.test(line)) {
    return "receipt_gl_account_unmapped";
  }
  const bill = /^Bill payment hold: ([a-z_]+)\./.exec(line);
  if (bill !== null) {
    const captured = bill[1];
    if (captured === undefined || !BILL_HOLD_REASONS.has(captured)) return null;
    return `bill_${captured}` as HoldReasonCode;
  }
  if (line.startsWith("shared card is not attached to the receipt company")) {
    return "receipt_card_not_attached";
  }
  if (line.startsWith("shared-card company is outside the intercompany set")) {
    return "receipt_shared_card_outside_set";
  }
  if (/^vendor identity .+ recognised; receipt needed before booking$/.test(line)) {
    return "receipt_needed_for_vendor";
  }
  return null;
}

/**
 * Raw hub account name after `receipt_gl_account_unmapped: ` on the first
 * useful line, or null. The caller hazard-strips this before it reaches a
 * face. A name that tries to smuggle a second line cannot: only line one is
 * read.
 */
export function holdReasonHubAccountName(
  text: string | null | undefined,
): string | null {
  const line = firstUsefulLine(text);
  if (line === null) return null;
  const match = /^receipt_gl_account_unmapped: (.*)$/.exec(line);
  if (match === null) return null;
  const name = (match[1] ?? "").trim();
  return name.length > 0 ? name : null;
}
